import React, { useEffect, useState } from "react";
import _ from "lodash";

import GenerateApiKey from "./components/GenerateApiKey";
import DeleteApiKey from "./components/DeleteApiKey";
import { Card, Grid, IconButton, Tooltip } from "@mui/material";
import SoftBox from "../../components/SoftBox";
import DataTable from "../../examples/Tables/DataTable";
import SoftTypography from "../../components/SoftTypography";
import { useGetApiKeys, useGetBrands } from "./hooks";
import SoftBadgeDot from "../../components/SoftBadgeDot";
import MainNavbar from "../../examples/Navbars/MainNavbar";
import MainLayout from "../../examples/LayoutContainers/MainLayout";
import BrandSelect from "../../components/BrandSelect";
import { getToken } from "../../utils/cache";
import ToggleApiKeyStatus from "./components/ToggleApiKeyStatus";
import { getMerchant } from "../../utils/localStorage";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { toastError, toastSuccess } from "../../utils/toast";

const ApiKeys = () => {
  const token = getToken();
  const merchant = getMerchant();
  const [apiKey, setApikey] = useState("");
  const [currentBrand, setCurrentBrand] = useState({});
  const { data: brands } = useGetBrands();
  useEffect(() => {
    if (!token) window.location.href = "/sign-in";
  }, []);

  useEffect(() => {
    if (brands?.length > 0) setCurrentBrand({ value: brands[0]._id, label: brands[0].name });
  }, [brands]);
  const { data: apiKeys } = useGetApiKeys(currentBrand.value);

  /** Copies the generated key and reports the clipboard result. */
  const handleCopyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toastSuccess({ description: "API key copied successfully" });
    } catch {
      toastError({ description: "Unable to copy API key. Please copy it manually." });
    }
  };

  const getRows = () => {
    return _.map(apiKeys, (item) => ({
      id: item.id,
      prefix: item.prefix,
      active: <SoftBadgeDot color={item.active ? "success" : "light"} />,
      actions: (
        <SoftBox display="flex" gap={1} sx={{ alignItems: "center" }}>
          <ToggleApiKeyStatus
            brandId={currentBrand.value}
            apiKeyId={item.id}
            active={item.active}
            prefix={item.prefix}
          />
          <DeleteApiKey brandId={currentBrand.value} apiKeyId={item.id} prefix={item.prefix} />
        </SoftBox>
      ),
    }));
  };

  return (
    <MainLayout>
      <MainNavbar />
      <SoftBox>
        <SoftTypography component="p" variant="caption" color="text" sx={{ mb: 2 }}>
          You can have up to 5 API keys{brands?.length > 1 ? " per brand" : ""}.
        </SoftTypography>
        <Grid
          container
          display="flex"
          justifyContent={brands?.length > 1 ? "space-between" : "flex-end"}
          alignItems="flex-end"
          gap={2}
          mb={3}
        >
          <BrandSelect brands={brands} value={currentBrand} onChange={setCurrentBrand} />
          <GenerateApiKey
            merchantId={merchant?._id}
            brandId={currentBrand.value}
            onSuccess={setApikey}
          />
        </Grid>
        {apiKey && (
          <Card sx={{ mt: 2 }}>
            <SoftBox p={3}>
              <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                API Key
              </SoftTypography>
              <SoftTypography
                component="p"
                variant="caption"
                fontWeight="regular"
                color="text"
                sx={{ mt: 0.5 }}
              >
                Copy and store this key securely. It will disappear when you leave or refresh this
                page.
              </SoftTypography>
              <SoftBox display="flex" alignItems="center" gap={1} mt={0.5}>
                <SoftTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  size="md"
                  sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {apiKey}
                </SoftTypography>
                <Tooltip title="Copy API key">
                  <IconButton
                    aria-label="Copy API key"
                    size="small"
                    onClick={handleCopyApiKey}
                    sx={{ flexShrink: 0 }}
                  >
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </SoftBox>
            </SoftBox>
          </Card>
        )}
        <SoftBox
          mt={2}
          sx={{
            "& .data-table-footer": { display: "none" },
            "& .MuiTableContainer-root": { borderRadius: "inherit" },
          }}
        >
          <DataTable
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            manualPagination
            table={{
              columns: [
                { Header: "id", accessor: "id" },
                { Header: "prefix", accessor: "prefix" },
                { Header: "active", accessor: "active" },
                { Header: "actions", accessor: "actions", align: "right" },
              ],
              rows: getRows(),
            }}
          />
        </SoftBox>
      </SoftBox>
    </MainLayout>
  );
};

export default ApiKeys;
